/**
 * Visualization Export Utility
 *
 * Two rendering paths for maximum quality:
 *
 * 1. SVG content (mermaid, raw SVG)
 *    → getBBox-cropped → canvas rasterization at 3× scale
 *
 * 2. HTML content (html, vega-lite, bpmn)
 *    → dom-to-image-more capture from iframe
 *
 * No animation / GIF support by design.
 *
 * Install:
 *   npm install dom-to-image-more
 */

import domtoimage from "dom-to-image-more";

// ─── SVG measurement & cropping ──────────────────────────────────────

function measureSvgContentBounds(
  svgString: string,
): { x: number; y: number; width: number; height: number } | null {
  const container = document.createElement("div");
  container.style.cssText =
    "position:fixed;left:-99999px;top:-99999px;visibility:hidden;pointer-events:none;";
  container.innerHTML = svgString;
  document.body.appendChild(container);

  try {
    const svgEl = container.querySelector("svg");
    if (!svgEl) return null;

    svgEl.style.width = "auto";
    svgEl.style.height = "auto";
    svgEl.style.maxWidth = "none";
    svgEl.style.maxHeight = "none";

    const bbox = (svgEl as unknown as SVGGraphicsElement).getBBox();
    return {
      x: bbox.x,
      y: bbox.y,
      width: bbox.width,
      height: bbox.height,
    };
  } catch {
    return null;
  } finally {
    document.body.removeChild(container);
  }
}

function cropSvgToContent(svgString: string, padding = 20): string {
  const bounds = measureSvgContentBounds(svgString);
  if (!bounds || bounds.width === 0 || bounds.height === 0) {
    return svgString;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");
  const svgEl = doc.documentElement;

  const vbX = bounds.x - padding;
  const vbY = bounds.y - padding;
  const vbW = bounds.width + padding * 2;
  const vbH = bounds.height + padding * 2;

  svgEl.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${vbH}`);
  svgEl.removeAttribute("width");
  svgEl.removeAttribute("height");

  return new XMLSerializer().serializeToString(svgEl);
}

// ─── SVG → PNG (high‑res rasterization) ───────────────────────────────

function svgToPngBlob(
  svgString: string,
  width: number,
  height: number,
  scale: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const base64 = btoa(unescape(encodeURIComponent(svgString)));
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(null);

      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => resolve(blob), "image/png");
    };

    img.onerror = () => resolve(null);
    img.src = `data:image/svg+xml;base64,${base64}`;
  });
}

// ─── PNG export API ──────────────────────────────────────────────────

/**
 * Capture SVG content as high‑resolution PNG.
 * Used for mermaid diagrams and raw SVG.
 */
export async function captureSvgAsPng(
  svgString: string,
  scale: number = 3,
): Promise<Blob | null> {
  const cropped = cropSvgToContent(svgString);
  const bounds = measureSvgContentBounds(cropped);
  if (!bounds) return null;

  const width = bounds.width + 40;  // padding already applied
  const height = bounds.height + 40;

  return svgToPngBlob(cropped, width, height, scale);
}

/**
 * Capture an iframe's rendered content as PNG.
 * Used for HTML, vega‑lite, BPMN previews.
 */
export async function captureIframeAsPng(
  iframe: HTMLIFrameElement,
): Promise<Blob> {
  const doc = iframe.contentDocument;
  if (!doc) {
    throw new Error("Cannot access iframe document");
  }

  const target = doc.body;
  const width = target.scrollWidth;
  const height = target.scrollHeight;

  const dataUrl = await domtoimage.toPng(target, {
    width,
    height,
    style: {
      transform: "none",
      margin: "0",
    },
  });

  const res = await fetch(dataUrl);
  return res.blob();
}

// ─── Download helpers ─────────────────────────────────────────────────

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadText(text: string, filename: string) {
  const blob = new Blob([text], {
    type: "text/plain;charset=utf-8",
  });
  downloadBlob(blob, filename);
}